import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Groups in display order
const GROUP_META = [
  { key: "anthropic", label: "Anthropic (Claude)", testIntegration: "anthropic", description: "Powers Arya's responses." },
  { key: "wati",      label: "WATI (WhatsApp)",     testIntegration: "wati",      description: "Inbound replies + hot-lead handoff messages." },
  { key: "alerts",    label: "Sales Alerts",        testIntegration: null,        description: "Who gets pinged when a hot lead is detected." },
  { key: "booking",   label: "Demo Booking",        testIntegration: null,        description: "URL Arya shares when offering a virtual demo." },
  { key: "smtp",      label: "Email (SMTP)",        testIntegration: "smtp",      description: "Used for hot-lead email alerts." }
];

function SourceBadge({ source }) {
  if (source === "db") return <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#E85D24]/15 text-[#E85D24] border border-[#E85D24]/30">DB override</span>;
  if (source === "env") return <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#1F1F1F] text-[#888] border border-[#2A2A2A]">.env</span>;
  return <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#FF3B30]/15 text-[#FF3B30] border border-[#FF3B30]/30">not set</span>;
}

function SettingRow({ setting, draftValue, onChange }) {
  return (
    <div className="grid grid-cols-12 gap-3 items-start py-3 border-b border-[#1A1A1A] last:border-b-0">
      <div className="col-span-4">
        <div className="text-sm text-white font-medium flex items-center gap-2">
          {setting.label}
          <SourceBadge source={setting.source} />
        </div>
        <div className="text-[11px] text-[#666] mt-0.5">{setting.description}</div>
        <code className="text-[10px] text-[#555] font-mono">{setting.key}</code>
      </div>
      <div className="col-span-4">
        <div className="text-[11px] text-[#888] mb-1">Current</div>
        <div className="text-xs font-mono text-[#A0A0A0] bg-[#0A0A0A] border border-[#1A1A1A] rounded px-2 py-1.5 truncate" data-testid={`current-${setting.key}`}>
          {setting.configured ? setting.display_value : <span className="text-[#555] italic">empty</span>}
        </div>
      </div>
      <div className="col-span-4">
        <div className="text-[11px] text-[#888] mb-1">New value {setting.sensitive ? "(will be encrypted at rest if you enable HTTPS-only cookies)" : ""}</div>
        <input
          type={setting.sensitive ? "password" : "text"}
          value={draftValue ?? ""}
          onChange={(e) => onChange(setting.key, e.target.value)}
          placeholder={setting.configured ? "Leave blank to keep current" : "Not set yet"}
          data-testid={`input-${setting.key}`}
          className="w-full bg-[#0A0A0A] border border-[#1F1F1F] focus:border-[#E85D24] rounded px-2 py-1.5 text-xs text-white font-mono outline-none transition-colors"
        />
      </div>
    </div>
  );
}

export default function AdminSettingsTab() {
  const [settings, setSettings] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState({});
  const [auditLog, setAuditLog] = useState([]);

  const fetchSettings = useCallback(async () => {
    try {
      const [s, a] = await Promise.all([
        axios.get(`${API}/settings`),
        axios.get(`${API}/settings/audit`).catch(() => ({ data: [] }))
      ]);
      setSettings(s.data.settings);
      setAuditLog(a.data || []);
    } catch (err) {
      console.error("Failed to load settings:", err);
      if (err.response?.status === 403) {
        toast.error("Admin access required to view settings");
      } else {
        toast.error("Failed to load settings");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const handleDraftChange = (key, value) => setDrafts((d) => ({ ...d, [key]: value }));

  const handleSave = async () => {
    // Only send keys the admin actually typed into.
    const updates = Object.fromEntries(
      Object.entries(drafts).filter(([, v]) => v !== undefined && v !== null && v !== "")
    );
    if (Object.keys(updates).length === 0) {
      toast.info("No changes to save");
      return;
    }
    setSaving(true);
    try {
      const res = await axios.put(`${API}/settings`, { updates });
      setSettings(res.data.settings);
      setDrafts({});
      toast.success(`Saved ${res.data.changed.length} setting${res.data.changed.length !== 1 ? "s" : ""}. Live integrations updated.`);
      // Refresh audit log
      const a = await axios.get(`${API}/settings/audit`).catch(() => ({ data: [] }));
      setAuditLog(a.data || []);
    } catch (err) {
      console.error("Save failed:", err);
      toast.error("Save failed: " + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async (integration) => {
    setTesting((t) => ({ ...t, [integration]: true }));
    // Pass any unsaved drafts as overrides so the admin can validate a new key BEFORE saving.
    const overrides = Object.fromEntries(
      Object.entries(drafts).filter(([, v]) => v !== undefined && v !== null && v !== "")
    );
    try {
      const res = await axios.post(`${API}/settings/test`, { integration, overrides });
      if (res.data.ok) toast.success(res.data.message);
      else toast.error(res.data.message || "Test failed");
    } catch (err) {
      console.error("Test failed:", err);
      toast.error("Test failed: " + (err.response?.data?.message || err.message));
    } finally {
      setTesting((t) => ({ ...t, [integration]: false }));
    }
  };

  if (loading) {
    return <div className="text-[#707070] text-sm" data-testid="settings-loading">Loading settings…</div>;
  }

  const groupedSettings = GROUP_META.map((g) => ({
    ...g,
    items: settings.filter((s) => s.group === g.key)
  }));

  const dirty = Object.values(drafts).some((v) => v && v.length > 0);

  return (
    <div data-testid="admin-settings-tab" className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-white">API Keys & Integration Settings</h2>
          <p className="text-xs text-[#888] mt-1 max-w-2xl leading-relaxed">
            Admin-only. Changes take effect immediately — no restart needed. Saved values override the values in <code className="text-[#E85D24] font-mono">.env</code>; clear a field and save to revert to the .env fallback. Sensitive values are masked.
          </p>
        </div>
        <button
          onClick={handleSave}
          disabled={!dirty || saving}
          data-testid="save-settings-btn"
          className="shrink-0 px-4 py-2 text-xs font-medium bg-[#E85D24] hover:bg-[#E85D24]/85 disabled:bg-[#333] disabled:cursor-not-allowed text-white rounded transition-colors"
        >
          {saving ? "Saving…" : dirty ? "Save changes" : "No changes"}
        </button>
      </div>

      {groupedSettings.map((group) => (
        <section key={group.key} data-testid={`settings-group-${group.key}`} className="border border-[#1A1A1A] rounded">
          <header className="px-4 py-3 bg-[#0F0F0F] border-b border-[#1A1A1A] flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold text-white">{group.label}</h3>
              <p className="text-[11px] text-[#666] mt-0.5">{group.description}</p>
            </div>
            <div className="flex items-center gap-2">
              {group.testIntegration && (
                <button
                  onClick={() => handleTest(group.testIntegration)}
                  disabled={!!testing[group.testIntegration]}
                  data-testid={`test-${group.testIntegration}-btn`}
                  className="px-3 py-1.5 text-[11px] font-medium bg-[#1A1A1A] hover:bg-[#252525] border border-[#2A2A2A] hover:border-[#E85D24] text-[#A0A0A0] hover:text-white rounded transition-colors disabled:opacity-50"
                >
                  {testing[group.testIntegration] ? "Testing…" : "Test connection"}
                </button>
              )}
              {group.key === "wati" && (
                <button
                  onClick={() => handleTest("wati_template")}
                  disabled={!!testing.wati_template}
                  data-testid="test-wati_template-btn"
                  title="Sends a real lead_handoff_alert template to SALES_ALERT_PHONE to verify expired-session fallback delivery."
                  className="px-3 py-1.5 text-[11px] font-medium bg-[#1A1A1A] hover:bg-[#252525] border border-[#2A2A2A] hover:border-[#E85D24] text-[#A0A0A0] hover:text-white rounded transition-colors disabled:opacity-50"
                >
                  {testing.wati_template ? "Sending…" : "Test template fallback"}
                </button>
              )}
            </div>
          </header>
          <div className="px-4">
            {group.items.length === 0 ? (
              <p className="py-4 text-[11px] text-[#555]">No keys in this group.</p>
            ) : (
              group.items.map((s) => (
                <SettingRow key={s.key} setting={s} draftValue={drafts[s.key]} onChange={handleDraftChange} />
              ))
            )}
          </div>
        </section>
      ))}

      <section className="border border-[#1A1A1A] rounded" data-testid="audit-log-section">
        <header className="px-4 py-3 bg-[#0F0F0F] border-b border-[#1A1A1A]">
          <h3 className="text-sm font-semibold text-white">Recent changes</h3>
          <p className="text-[11px] text-[#666] mt-0.5">Last 50 setting modifications. Values are NOT logged — only metadata.</p>
        </header>
        {auditLog.length === 0 ? (
          <p className="px-4 py-5 text-[11px] text-[#555]">No changes yet.</p>
        ) : (
          <ul className="divide-y divide-[#1A1A1A]">
            {auditLog.map((a) => (
              <li key={a.id} className="px-4 py-2 text-[11px] text-[#A0A0A0] flex items-center justify-between">
                <span>
                  <span className="text-[#E85D24] font-mono">{a.actor_email}</span>{" "}
                  <span className={a.action === "cleared" ? "text-[#FF3B30]" : "text-[#32D74B]"}>{a.action}</span>{" "}
                  <code className="text-[10px] text-[#888] font-mono">{a.key}</code>
                </span>
                <span className="text-[10px] text-[#666]">{new Date(a.created_at).toLocaleString("en-IN")}</span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
